'use client';

import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import {
  canTrackSnowplowPageLeave,
  createPageLeaveEventOnce,
  resetPageLeaveState,
  type PageLeaveSnapshotSource,
  type PageLeaveState,
  type PageLeaveTrigger,
} from './snowplowPageLeaveState';

function getBrowserPageLeaveSource(): PageLeaveSnapshotSource | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  return {
    location: window.location,
    document,
    now: Date.now,
  };
}

export function useSnowplowPageLeaveTracking(): void {
  const pathname = usePathname();
  const { trackEvent } = useReporting();
  const stateRef = useRef<PageLeaveState>({ snapshot: null });
  const lastPathnameRef = useRef<string | null>(null);

  const sendPageLeave = useCallback(
    (trigger: PageLeaveTrigger, nextUrlPath?: string) => {
      if (!canTrackSnowplowPageLeave()) {
        return;
      }

      const source = getBrowserPageLeaveSource();
      if (!source) {
        return;
      }

      try {
        const event = createPageLeaveEventOnce(stateRef.current, trigger, source, nextUrlPath);

        if (event) {
          trackEvent(event, [ProviderType.SNOWPLOW]);
        }
      } catch (error) {
        logger.error('Snowplow PageLeave tracking error:', error);
      }
    },
    [trackEvent],
  );

  useEffect(() => {
    if (!canTrackSnowplowPageLeave()) {
      return;
    }

    const source = getBrowserPageLeaveSource();
    if (!source) {
      return;
    }

    if (!stateRef.current.snapshot) {
      resetPageLeaveState(stateRef.current, source);
      lastPathnameRef.current = pathname;
      return;
    }

    if (lastPathnameRef.current === null) {
      lastPathnameRef.current = pathname;
      return;
    }

    if (lastPathnameRef.current === pathname) {
      return;
    }

    sendPageLeave('pathname_change', pathname);

    const nextSource = getBrowserPageLeaveSource();
    if (nextSource) {
      resetPageLeaveState(stateRef.current, nextSource);
    }

    lastPathnameRef.current = pathname;
  }, [pathname, sendPageLeave]);

  useEffect(() => {
    if (!canTrackSnowplowPageLeave()) {
      return;
    }

    if (!stateRef.current.snapshot) {
      const source = getBrowserPageLeaveSource();
      if (source) {
        resetPageLeaveState(stateRef.current, source);
      }
    }

    const handleBeforeUnload = () => {
      sendPageLeave('beforeunload');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendPageLeave]);
}
